export type RentalComparable = {
  id: number;
  address: string;
  surface: number;
  rooms: number | null;
  netMonthly: number;
  chargesMonthly: number | null;
  lat?: number | null;
  lng?: number | null;
};

const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const toCoordinates = (lat: unknown, lng: unknown) =>
  finite(lat) && finite(lng) ? { lat, lng } : { lat: null, lng: null };

export const toRentalComparable = (listing: {
  id: number;
  address?: string | null;
  locality?: string | null;
  computed_surface?: number | null;
  number_of_rooms?: number | null;
  rent_net_monthly?: number | null;
  gross_rent_monthly?: number | null;
  geog?: unknown;
}): RentalComparable | null => {
  if (
    !positive(listing.computed_surface) ||
    !positive(listing.rent_net_monthly)
  ) {
    return null;
  }
  const gross = listing.gross_rent_monthly;
  const point =
    typeof listing.geog === 'object' &&
    listing.geog != null &&
    'coordinates' in listing.geog &&
    Array.isArray(listing.geog.coordinates)
      ? listing.geog.coordinates
      : [];
  return {
    id: listing.id,
    address: listing.address || listing.locality || '',
    surface: listing.computed_surface,
    rooms: positive(listing.number_of_rooms) ? listing.number_of_rooms : null,
    netMonthly: listing.rent_net_monthly,
    chargesMonthly:
      positive(gross) && gross >= listing.rent_net_monthly
        ? gross - listing.rent_net_monthly
        : null,
    ...toCoordinates(point[1], point[0]),
  };
};

export const readRentalComparables = (value: unknown): RentalComparable[] => {
  if (!Array.isArray(value)) return [];
  const valid = value.filter(
    (item): item is RentalComparable =>
      item != null &&
      Number.isInteger(item.id) &&
      typeof item.address === 'string' &&
      positive(item.surface) &&
      positive(item.netMonthly) &&
      (item.rooms === null || positive(item.rooms)) &&
      (item.chargesMonthly === null ||
        (typeof item.chargesMonthly === 'number' &&
          Number.isFinite(item.chargesMonthly) &&
          item.chargesMonthly >= 0)),
  );
  return valid.map(item => ({ ...item, ...toCoordinates(item.lat, item.lng) }));
};

export const netRentPerSqm = (
  comparable: Pick<RentalComparable, 'netMonthly' | 'surface'>,
): number => comparable.netMonthly / comparable.surface;

export const medianNetRentPerSqm = (
  comparables: RentalComparable[],
): number | null => {
  if (comparables.length === 0) return null;
  const prices = comparables.map(netRentPerSqm).sort((a, b) => a - b);
  const middle = Math.floor(prices.length / 2);
  return prices.length % 2
    ? prices[middle]!
    : (prices[middle - 1]! + prices[middle]!) / 2;
};

export const estimateMonthlyNetRent = (
  comparables: RentalComparable[],
  surface: number | null | undefined,
): number | null => {
  const median = medianNetRentPerSqm(comparables);
  if (!positive(surface) || median == null) return null;
  return Math.round(median * surface);
};

export const resolveMonthlyRent = ({
  avm,
  comparables,
  includeComparables,
  override,
}: {
  avm: number | null | undefined;
  comparables: number | null | undefined;
  includeComparables: boolean;
  override: number | null | undefined;
}) => {
  const avmRent = positive(avm) ? avm : null;
  const comparableRent =
    includeComparables && positive(comparables) ? comparables : null;
  const suggested =
    avmRent != null && comparableRent != null
      ? Math.round((avmRent + comparableRent) / 2)
      : avmRent ?? comparableRent;
  const followsAvmDefault =
    avmRent != null &&
    comparableRent != null &&
    typeof override === 'number' &&
    Math.abs(override - avmRent) <= 0.5;
  const final =
    typeof override === 'number' &&
    Number.isFinite(override) &&
    override >= 0 &&
    !followsAvmDefault
      ? override
      : suggested;
  return { avm: avmRent, comparables: comparableRent, suggested, final };
};

export const calculateCaprateMinimumRequiredYield = (
  caprateMinimumRequiredYield: number | null | undefined,
  rentValue: number | null | undefined,
  propertyValue: number | null | undefined,
): number | null => {
  if (caprateMinimumRequiredYield != null) {
    return caprateMinimumRequiredYield;
  }

  if (rentValue != null && propertyValue != null && propertyValue > 0) {
    return ((12 * rentValue) / propertyValue) * 100;
  }

  return null;
};

export const hasRentComparison = ({
  avm,
  comparables,
}: ReturnType<typeof resolveMonthlyRent>) => avm != null && comparables != null;
